'use client';

import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DeleteTeamButton } from './DeleteTeamButton';
import { PlayerCard } from './PlayerCard';
import {
  Registration,
  Team,
  Gender,
  RegistrationPaymentType,
  RegistrationStatus,
  type PaymentType,
  type Player,
} from '@/types';
import { getGenderLabel } from '@/utils/gender-utils';
import { getRegistrationAbonoLabel } from '@/utils/payment-utils';
import { PlayerInfoPopover } from '../../PlayerInfoPopover';
import { AvailabilitySection } from './AvailabilitySection';
import type { Availability } from '@/types/tournament.model';
import type { EloCategory } from '../../models/elo.model';
import { ELO_RATING_BADGE_CLASSNAME, formatEloBadgeLabel } from '../../utils';
import { formatUtcDateTimeForUser } from '@/utils/formatters';

/** Compact text inside parentheses, e.g. "Cash/transfer". */
function compactAbonoForBadge(label: string): string {
  return label.replace(/\s*\/\s*/g, '/').trim();
}

/** When applicable: "Paid (Cash/transfer)"; otherwise status label only. */
function registrationStatusDisplayText(
  status: RegistrationStatus,
  statusLabel: string,
  abonoLabel: string | undefined,
): string {
  if (status === RegistrationStatus.COMPLETE && abonoLabel) {
    return `${statusLabel} (${compactAbonoForBadge(abonoLabel)})`;
  }
  return statusLabel;
}

interface RegistrationCardProps {
  registration: Registration & { originalIndex?: number };
  index: number;
  sportUid: string;
  eloCategories: EloCategory[];
  /** Tournament payment method(s) accepted (fallback when the API omits per-registration `paymentMethod`). */
  tournamentPaymentType?: PaymentType;
  isExpanded: boolean;
  toggleRegistration: () => void;
  getTeamAverageElo: (team: Team) => number;
  getTeamGender: (team: Team) => Gender | null;
  getPlayerInitials: (name: string) => string;
  formatWhatsAppNumber: (phone: string) => string;
  getRegistrationStatusLabel: (status: string) => string;
  getRegistrationStatusColor: (status: string) => string;
  tournamentSchedule?: Availability[];
  /** If present, enables replacing players in this registration. */
  onReplacePlayer?: (player: Player) => void;
  /** If present, enables recording a payment a player made outside the app. */
  onMarkPlayerPaid?: (player: Player) => void;
  /** If present, enables undoing a payment the manager recorded by hand. */
  onUndoPlayerPayment?: (player: Player) => void;
  /** If present, enables editing the team's availability. */
  onEditAvailability?: () => void;
  onDelete?: () => void;
  canDelete?: boolean;
}

export function RegistrationCard({
  registration,
  index,
  sportUid,
  eloCategories,
  tournamentPaymentType,
  isExpanded,
  toggleRegistration,
  getTeamAverageElo,
  getTeamGender,
  getPlayerInitials,
  formatWhatsAppNumber,
  getRegistrationStatusLabel,
  getRegistrationStatusColor,
  tournamentSchedule,
  onReplacePlayer,
  onMarkPlayerPaid,
  onUndoPlayerPayment,
  onEditAvailability,
  onDelete,
  canDelete,
}: RegistrationCardProps) {
  const { team } = registration;
  const teamElo = getTeamAverageElo(team);
  const teamGender = getTeamGender(team);
  const availability = registration.availability ?? [];
  const abonoLabel = getRegistrationAbonoLabel(
    registration,
    tournamentPaymentType,
  );
  const registrationDate = formatUtcDateTimeForUser(registration.createdAt);
  // Per-player payment state only means something when each player pays their own half.
  const isIndividualPayment =
    registration.paymentType === RegistrationPaymentType.INDIVIDUAL;
  // Emails arrive from the API, so they are compared normalized like the sibling components do.
  const payedBy = new Set(
    (registration.payedBy ?? []).map((email) => email.toLowerCase()),
  );
  const managerPayedBy = new Set(
    (registration.managerPayedBy ?? []).map((email) => email.toLowerCase()),
  );
  // The two status allowlists the backend collects on: PENDING_PAYMENT/PARTIALLY_PAID take a new
  // payment, those plus COMPLETE allow undoing one. PENDING_ADMIN_CHECK is deliberately in neither:
  // in a STRICT tournament nobody can pay until the organizer accepts the registration, so there is
  // no payment state to show yet.
  const acceptsNewPayments =
    registration.status === RegistrationStatus.PENDING_PAYMENT ||
    registration.status === RegistrationStatus.PARTIALLY_PAID;
  const isSettled = registration.status === RegistrationStatus.COMPLETE;
  // Cancelled/rejected registrations have nothing left to collect, so per-player payment state is noise.
  const showsPaymentState =
    isIndividualPayment && (acceptsNewPayments || isSettled);
  // Replacing a player who had already paid keeps their payment (their half is not charged twice),
  // and the payment keeps their email: the API answers "who paid" with somebody who is no longer on
  // the team. Nothing on this side can tell which of the current players still owes, and offering
  // "marcar como pagado" on both books one payment too many — the backend confirms a registration as
  // soon as it counts one payment per player, teammate paid or not. So when a paid half can't be
  // attributed, the card states how many halves are settled instead of guessing a per-player badge,
  // and offers no "marcar como pagado" (undo still reads managerPayedBy, which is exact).
  const settledHalves = payedBy.size;
  const attributableHalves = team.players.filter((player) =>
    payedBy.has(player.email.toLowerCase()),
  ).length;
  const hasUnattributableHalf = attributableHalves < settledHalves;
  const showsPerPlayerPaymentState =
    showsPaymentState && !hasUnattributableHalf;
  const canMarkPaid =
    isIndividualPayment && acceptsNewPayments && !hasUnattributableHalf;
  // Undo is driven by managerPayedBy, which only ever names a current player, so a replacement
  // cannot misdirect it. COMPLETE stays in: a half the organizer marked by mistake is undoable even
  // when it closed the registration, which is what the backend's UNDOABLE_STATUSES allows.
  const canUndoPayment =
    isIndividualPayment && (acceptsNewPayments || isSettled);
  const statusLabel = getRegistrationStatusLabel(registration.status);
  const statusBadgeText = registrationStatusDisplayText(
    registration.status,
    statusLabel,
    abonoLabel,
  );

  return (
    <div className="overflow-hidden rounded-lg border">
      <div
        className="flex cursor-pointer flex-wrap items-center justify-between bg-gray-50 p-4"
        onClick={toggleRegistration}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 shrink-0 text-gray-500" />
          ) : (
            <ChevronRight className="h-5 w-5 shrink-0 text-gray-500" />
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <h3 className="whitespace-nowrap font-semibold">
              Equipo #{registration.originalIndex || index + 1}
            </h3>
            {registrationDate && (
              <span className="text-xs text-gray-500">
                Inscripto el {registrationDate}
              </span>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              {/* Status + payment method in one badge when paid */}
              <Badge
                className={`shrink-0 ${getRegistrationStatusColor(registration.status)}`}
              >
                {statusBadgeText}
              </Badge>

              {/* Payment Type Badge */}
              <Badge variant="outline" className="shrink-0">
                {registration.paymentType === RegistrationPaymentType.INDIVIDUAL
                  ? 'Pago Individual'
                  : 'Pago por Equipo'}
              </Badge>

              {teamElo > 0 && (
                <Badge className={ELO_RATING_BADGE_CLASSNAME}>
                  {formatEloBadgeLabel(teamElo, eloCategories)}
                </Badge>
              )}

              {teamGender && (
                <Badge variant="outline" className="shrink-0">
                  {getGenderLabel(teamGender as Gender)}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Capped so long names wrap here instead of squeezing the team's column and its badges. */}
        <div className="hidden items-center gap-2 sm:ml-4 sm:flex sm:max-w-[50%] sm:flex-wrap">
          <span className="mr-1 text-sm text-gray-500">Jugadores:</span>
          {team.players.map((player, playerIndex) => (
            <PlayerInfoPopover
              key={playerIndex}
              player={player}
              sportUid={sportUid}
              eloCategories={eloCategories}
            >
              <span className="cursor-pointer text-sm font-medium hover:text-blue-600 hover:underline">
                {player.name}
                {playerIndex < team.players.length - 1 ? ',' : ''}
              </span>
            </PlayerInfoPopover>
          ))}
        </div>

        <DeleteTeamButton canDelete={canDelete} onDelete={onDelete} />
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4">
          {/* Registration Details */}
          <div className="mb-4 rounded-lg bg-gray-50 p-3">
            <h4 className="mb-2 font-semibold text-gray-900">
              Detalles de Registración
            </h4>
            <div className="grid gap-2 text-sm">
              <div className="flex flex-wrap justify-between gap-x-3">
                <span className="text-gray-600">ID de Registración:</span>
                <span className="break-all font-mono text-xs">
                  {registration.id}
                </span>
              </div>
              <div className="flex flex-wrap justify-between gap-x-3">
                <span className="text-gray-600">Estado:</span>
                <Badge
                  className={`${getRegistrationStatusColor(registration.status)}`}
                >
                  {statusBadgeText}
                </Badge>
              </div>
              {registrationDate && (
                <div className="flex flex-wrap justify-between gap-x-3">
                  <span className="text-gray-600">Fecha de Inscripción:</span>
                  <span>{registrationDate}</span>
                </div>
              )}
              <div className="flex flex-wrap justify-between gap-x-3">
                <span className="text-gray-600">Tipo de Pago:</span>
                <span>
                  {registration.paymentType ===
                  RegistrationPaymentType.INDIVIDUAL
                    ? 'Individual'
                    : 'Por Equipo'}
                </span>
              </div>
            </div>
          </div>

          {/* Team Players */}
          <div>
            <h4 className="mb-3 font-semibold text-gray-900">
              Jugadores del Equipo
            </h4>
            {showsPaymentState && hasUnattributableHalf && (
              <p className="mb-3 text-sm text-amber-700">
                Esta inscripción tiene {settledHalves} de {team.players.length}{' '}
                pagos registrados, pero uno quedó a nombre de un jugador que fue
                reemplazado: no se puede saber a quién le falta pagar, así que
                el panel no ofrece marcar pagos sobre esta pareja.
              </p>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {team.players.map((player, playerIndex) => {
                const playerEmail = player.email.toLowerCase();
                // A settled registration owes nothing, whoever the payments are booked against: a
                // cash one closes on a single payment by design, so payedBy alone would read as if
                // the teammate still owed their half.
                const hasPaid = isSettled || payedBy.has(playerEmail);
                const paidByManager = managerPayedBy.has(playerEmail);

                return (
                  <PlayerCard
                    key={player.id || `player-${playerIndex}`}
                    player={player}
                    playerIndex={playerIndex}
                    sportUid={sportUid}
                    eloCategories={eloCategories}
                    getPlayerInitials={getPlayerInitials}
                    formatWhatsAppNumber={formatWhatsAppNumber}
                    onReplace={
                      onReplacePlayer
                        ? () => onReplacePlayer(player)
                        : undefined
                    }
                    hasPaid={showsPerPlayerPaymentState ? hasPaid : undefined}
                    onMarkPaid={
                      canMarkPaid && !hasPaid && onMarkPlayerPaid
                        ? () => onMarkPlayerPaid(player)
                        : undefined
                    }
                    onUndoPayment={
                      canUndoPayment && paidByManager && onUndoPlayerPayment
                        ? () => onUndoPlayerPayment(player)
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </div>

          {/* Availability */}
          <AvailabilitySection
            availability={availability}
            schedule={tournamentSchedule}
            className="mt-6"
            onEdit={onEditAvailability}
          />
        </div>
      )}
    </div>
  );
}
